import { computed, type Ref, ref, watch } from 'vue';
import { generateInsights, regenerateInsights } from '@/api/insights';
import type { InsightAdjustmentRegenerateTrackingData } from '@/types/insights';
import { useSwrPolling } from '@/utils/composables/useSwrPolling';
import {
  insightPayloadAxes,
  normalizeInsight,
} from '@/utils/insights/insightAxis';
import { STATES, useInsights, useInsightStatus } from '@/utils/swr';

export const INSIGHTS_POLLING_INTERVAL_MS = 2000;

export const useInsightsGeneration = (enabled: Ref<boolean>) => {
  const { data: response, mutate, error, state } = useInsights(enabled);
  const data = computed(() =>
    response.value === undefined ? undefined : normalizeInsight(response.value),
  );

  // 生成の開始をバッジへ伝えるため、表示はしないが同じキーを購読して取り直す。
  const { mutate: revalidateInsightStatus } = useInsightStatus(enabled);

  const isFetching = computed(
    () => state.value === STATES.PENDING || state.value === STATES.VALIDATING,
  );
  const latestStatus = computed(
    () => data.value?.latest_attempt.generation_status,
  );

  const isRegenerating = ref(false);
  const pendingRegenerationUuid = ref<string | null>(null);
  const isGenerating = computed(
    () =>
      isRegenerating.value ||
      latestStatus.value === 'unprocessed' ||
      latestStatus.value === 'processing',
  );
  const isGenerated = computed(() => latestStatus.value === 'processed');
  // 軸単位の公開では本体が返った後も残りの軸が生成中なので、全軸が確定するまで取り続ける
  const isProcessing = computed(() => {
    if (latestStatus.value === 'processing') return true;
    const payload = data.value?.payload;
    return (
      payload !== undefined &&
      insightPayloadAxes(payload).some(
        axis => axis.generation_status === 'processing',
      )
    );
  });

  // 楽観フラグが立っている間もポーリングを続け、サーバ側の状態遷移を確実に拾う
  const shouldPoll = computed(() => isProcessing.value || isRegenerating.value);
  useSwrPolling(mutate, shouldPoll, INSIGHTS_POLLING_INTERVAL_MS);

  watch(
    [() => data.value?.latest_attempt.uuid, pendingRegenerationUuid],
    ([latestUuid, pendingUuid]) => {
      if (pendingUuid !== null && latestUuid === pendingUuid) {
        isRegenerating.value = false;
        pendingRegenerationUuid.value = null;
      }
    },
  );

  // エラー時のリトライ。初回生成と同じ POST /insights を叩き直す
  const retry = async () => {
    await generateInsights();
    void revalidateInsightStatus();
    await mutate();
  };

  // 初回生成の重複POSTを防ぐガード。下の初回生成 watch が参照する
  let generationRequested = false;

  const regenerate = async (
    trackingData?: InsightAdjustmentRegenerateTrackingData,
  ) => {
    isRegenerating.value = true;
    // 再生成でサーバ状態が 'unprocessed' に戻った際、下の初回生成 watch が
    // POST /insights を余計に叩かないよう、リクエスト済みとして扱う
    generationRequested = true;
    try {
      const { uuid } = await regenerateInsights(trackingData);
      pendingRegenerationUuid.value = uuid;
      void revalidateInsightStatus();
      await mutate();
    } catch (e) {
      isRegenerating.value = false;
      pendingRegenerationUuid.value = null;
      throw e;
    }
  };

  watch(
    () => [enabled.value, latestStatus.value] as const,
    async ([isEnabled, status]) => {
      if (!isEnabled) {
        generationRequested = false;
        return;
      }
      if (status === 'unprocessed' && !generationRequested) {
        generationRequested = true;
        await generateInsights();
        void revalidateInsightStatus();
        await mutate();
      }
    },
    { immediate: true },
  );

  let autoRegenerateRequested = false;
  watch(
    () => [enabled.value, data.value?.auto_regeneratable] as const,
    async ([isEnabled, autoRegeneratable]) => {
      if (!isEnabled || !autoRegeneratable) {
        autoRegenerateRequested = false;
        return;
      }
      if (!autoRegenerateRequested) {
        autoRegenerateRequested = true;
        try {
          await regenerate();
        } catch {
          // 自動再生成の失敗はエラー状態としての通常フローに委ねる
        }
      }
    },
    { immediate: true },
  );

  const regeneratable = computed(() => data.value?.regeneratable === true);

  return {
    data,
    error,
    mutate,
    retry,
    regenerate,
    isFetching,
    isGenerating,
    isRegenerating,
    isGenerated,
    regeneratable,
  };
};
